import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  User,
  Activity,
  Grid,
  Loader2,
} from "lucide-react";
import { createAthleteFormDefaults } from "../../models";
import { useUpdateAthlete } from "../../hooks/queries/useAthletes";
import { usePatchTrackingDay } from "../../hooks/queries/useAthleteData";
import { localDateKey } from "../../utils/localDate";
import { mergeUntouched } from "../../utils/formSync";
import {
  useSportFieldSports,
  useSportTrainingFields,
} from "../../hooks/queries/useSportFields";
import {
  FOOTBALL_POSITIONS,
  FOOTBALL_SPORT_LABEL,
  buildPositionFields,
  footballPositionLabel,
  isFootballAthlete,
  positionCodeFromText,
} from "../../constants/footballTaxonomy";
import TagSelector from "./TagSelector";

const getLatestTrackingRecord = (data) => {
  const records = Array.isArray(data?.athlete_tracking)
    ? [...data.athlete_tracking]
    : [];

  if (records.length === 0) return null;

  records.sort((a, b) => {
    const dateA = new Date(a?.record_date || 0).getTime();
    const dateB = new Date(b?.record_date || 0).getTime();
    return dateB - dateA;
  });

  return records[0];
};

const getTrainingSnapshotFromMetrics = (metrics, sport) => {
  if (!sport) return {};

  const bySport = metrics?.training?.bySport;
  const sportFields = bySport?.[sport]?.fields;

  if (!sportFields || typeof sportFields !== "object") return {};

  return Object.entries(sportFields).reduce((accumulator, [fieldId, field]) => {
    accumulator[fieldId] =
      field?.latest?.value === null || field?.latest?.value === undefined
        ? ""
        : String(field.latest.value);
    return accumulator;
  }, {});
};

const getTrainingSnapshot = (data, sport) => {
  const latestTracking = getLatestTrackingRecord(data);
  return getTrainingSnapshotFromMetrics(latestTracking?.metrics, sport);
};

const parseAthleteAttributes = (attributes) =>
  attributes && typeof attributes === "object" ? attributes : {};

const calculateBmi = (weight, height) => {
  const numericWeight = Number(weight);
  const numericHeight = Number(height);

  if (!numericWeight || !numericHeight || numericHeight <= 0) return "";

  const heightInMeters = numericHeight / 100;
  return (numericWeight / (heightInMeters * heightInMeters)).toFixed(1);
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const CITY_OPTIONS_BY_COUNTRY = {
  colombia: [
    "Bogota",
    "Medellin",
    "Cali",
    "Barranquilla",
    "Cartagena",
    "Bucaramanga",
    "Pereira",
    "Manizales",
    "Armenia",
    "Santa Marta",
    "Ibague",
    "Cucuta",
    "Pasto",
    "Villavicencio",
    "Monteria",
    "Neiva",
    "Tunja",
    "Popayan",
  ],
  mexico: [
    "Ciudad de Mexico",
    "Guadalajara",
    "Monterrey",
    "Puebla",
    "Tijuana",
    "Leon",
    "Queretaro",
    "Merida",
    "Cancun",
    "Toluca",
  ],
  argentina: [
    "Buenos Aires",
    "Cordoba",
    "Rosario",
    "Mendoza",
    "La Plata",
    "Mar del Plata",
    "Salta",
    "Santa Fe",
  ],
  chile: [
    "Santiago",
    "Valparaiso",
    "Concepcion",
    "La Serena",
    "Antofagasta",
    "Temuco",
    "Vina del Mar",
  ],
  peru: [
    "Lima",
    "Arequipa",
    "Trujillo",
    "Chiclayo",
    "Cusco",
    "Piura",
    "Iquitos",
    "Huancayo",
  ],
  ecuador: [
    "Quito",
    "Guayaquil",
    "Cuenca",
    "Manta",
    "Ambato",
    "Loja",
    "Machala",
  ],
  espana: [
    "Madrid",
    "Barcelona",
    "Valencia",
    "Sevilla",
    "Zaragoza",
    "Malaga",
    "Murcia",
    "Bilbao",
  ],
  "estados unidos": [
    "New York",
    "Los Angeles",
    "Chicago",
    "Houston",
    "Phoenix",
    "Philadelphia",
    "San Antonio",
    "San Diego",
    "Miami",
  ],
};

const REQUIRED_PERSONAL_FIELDS = new Set([
  "nombreCompleto",
  "fechaNacimiento",
  "edad",
  "peso",
  "altura",
  "deporte",
  "posicion",
  "nivel",
  "email",
  "nacionalidad",
  "ciudad",
]);

const RequiredMark = () => <span className="ml-1 text-red-500">*</span>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AthleteForm = ({
  onBack,
  initialData = {},
  initialTab = "personal",
  formMode = "full",
  athleteId,
  // Called after a successful save. Screens get fresh data from the refetch the
  // mutations trigger; this only lets a host refresh anything else it shows.
  onSaved,
  // Optional override for the daily-tracking section write. Defaults to the
  // session-scoped athlete hook; the admin panel passes an id-scoped mutateAsync
  // (useAdminPatchTracking) so an admin can edit another athlete's metrics.
  patchTrackingFn,
  // Optional override for the profile save. Defaults to PATCH /athletes/:id;
  // the admin panel passes a fn that routes through PATCH /admin/users/:id so
  // the edit is audited and updates the account atomically.
  updateAthleteFn,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [showPersonalRequiredErrors, setShowPersonalRequiredErrors] =
    useState(false);

  // Map athlete row shape to form fields
  const buildFormData = (data) => {
    const safe = data || {};
    const attrs = parseAthleteAttributes(safe.attributes);
    const derivedBmi = attrs.imc || calculateBmi(attrs.weight, attrs.height);

    return createAthleteFormDefaults({
      name: safe.full_name || "",
      age: attrs.age || "",
      weight: attrs.weight || "",
      height: attrs.height || "",
      bmi: derivedBmi,
      // Solo fútbol: el deporte no se elige (perfiles legados de otros
      // deportes pasan a fútbol al guardar).
      sport: FOOTBALL_SPORT_LABEL,
      position: footballPositionLabel(safe.position || attrs.position || ""),
      level: safe.category || "",
      // Prefer the real columns (returned by the admin detail endpoint) over the
      // jsonb mirror, so stored values don't render blank and block required-field
      // validation.
      birthDate: safe.birth_date || attrs.birth_date || "",
      gender: attrs.gender || "",
      experience: attrs.experience || "",
      email: safe.email || attrs.email || "",
      phone: attrs.phone || "",
      nationality: safe.country || attrs.country || "",
      city: attrs.city || "",
      guardianName: safe.guardian_name || "",
      guardianEmail: safe.guardian_email || "",
    });
  };

  const [personalData, setPersonalData] = useState(() =>
    buildFormData(initialData),
  );
  const [trainingData, setTrainingData] = useState({});

  const updateAthlete = useUpdateAthlete();
  const patchTrackingDay = usePatchTrackingDay();
  // Admin edits pass patchTrackingFn to target another athlete; otherwise use
  // the session-scoped hook (the athlete editing their own tracking).
  const doPatchTracking = patchTrackingFn || patchTrackingDay.mutateAsync;
  // Admin edits pass updateAthleteFn (audited, atomic account+profile); athletes
  // editing themselves fall through to PATCH /athletes/:id.
  const doUpdateAthlete = updateAthleteFn || updateAthlete.mutateAsync;
  // Tags de fútbol (Scout QA #24), sembrados desde attributes.tags.
  const [tags, setTags] = useState(
    () => parseAthleteAttributes(initialData?.attributes).tags || [],
  );
  const sportsQuery = useSportFieldSports();
  const trainingFieldsQuery = useSportTrainingFields();

  const trainingFieldsFromDb = useMemo(() => {
    const sports = sportsQuery.data || [];
    const fields = trainingFieldsQuery.data || [];

    const sportById = sports.reduce((accumulator, sport) => {
      accumulator[sport.id] = sport.name;
      return accumulator;
    }, {});

    return fields.reduce((accumulator, field) => {
      const sportName = sportById[field.sport_id];
      if (!sportName) return accumulator;

      if (!accumulator[sportName]) {
        accumulator[sportName] = [];
      }

      accumulator[sportName].push({
        id: field.field_key,
        title: field.title,
        subtitle: field.subtitle || "",
        unit: field.unit || "",
        inputType: field.input_type || "text",
        required: Boolean(field.required),
      });

      return accumulator;
    }, {});
  }, [sportsQuery.data, trainingFieldsQuery.data]);

  const normalizeValue = (value) =>
    value === null || value === undefined ? "" : String(value).trim();

  const isPersonalFieldMissing = (field) =>
    REQUIRED_PERSONAL_FIELDS.has(field) && normalizeValue(personalData?.[field]) === "";

  // A minor's guardian is where scouts' contact requests go for approval, so
  // for minors both guardian fields are required and the email must be usable.
  const isMinorProfile =
    Number(personalData?.edad) > 0 && Number(personalData?.edad) < 18;
  const guardianEmailValue = normalizeValue(personalData?.guardianEmail).toLowerCase();
  const guardianNameError =
    isMinorProfile && normalizeValue(personalData?.guardianName) === ""
      ? "Campo obligatorio"
      : "";
  const guardianEmailError = !isMinorProfile
    ? ""
    : guardianEmailValue === ""
      ? "Campo obligatorio"
      : !EMAIL_PATTERN.test(guardianEmailValue)
        ? "Revisa el correo: no parece válido."
        : guardianEmailValue === normalizeValue(personalData?.email).toLowerCase()
          ? "Debe ser el correo de tu tutor, no el tuyo."
          : "";

  const inputClassName = (isMissing, extra = "") =>
    `${extra} px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-gray-900 ${
      isMissing
        ? "border-red-400 bg-red-50 focus:ring-red-200"
        : "border-gray-300 focus:ring-athletain-blue"
    }`;

  const requiredHint = (isMissing) =>
    isMissing ? (
      <p className="mt-2 text-xs font-medium text-red-600">Campo obligatorio</p>
    ) : null;

  const parseTrainingValue = (rawValue, inputType) => {
    const normalized = normalizeValue(rawValue);
    if (normalized === "") return "";

    if (inputType === "number") {
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : normalized;
    }

    return normalized;
  };

  const hasPersonalChanges = useMemo(() => {
    const baseline = buildFormData(initialData);
    const keys = Object.keys(baseline);
    const baselineTags = parseAthleteAttributes(initialData?.attributes).tags || [];
    return (
      keys.some(
        (key) =>
          normalizeValue(personalData?.[key]) !== normalizeValue(baseline?.[key]),
      ) ||
      JSON.stringify(tags) !== JSON.stringify(baselineTags) ||
      // The form always shows Fútbol; a profile still stored under another
      // sport needs a save to move it (and to become visible to scouts).
      (Boolean(initialData) && !isFootballAthlete(initialData))
    );
  }, [initialData, personalData, tags]);

  // Refetches (after any save, on focus or app resume) must not wipe edits in
  // progress: only untouched fields and tags follow the server.
  const personalBaselineRef = useRef(personalData);
  const tagsBaselineRef = useRef(tags);
  const trainingBaselineRef = useRef(trainingData);

  useEffect(() => {
    const next = buildFormData(initialData);
    const previous = personalBaselineRef.current;
    personalBaselineRef.current = next;
    setPersonalData((current) => mergeUntouched(current, previous, next));

    const nextTags = parseAthleteAttributes(initialData?.attributes).tags || [];
    const previousTags = tagsBaselineRef.current;
    tagsBaselineRef.current = nextTags;
    setTags((current) =>
      JSON.stringify(current) === JSON.stringify(previousTags) ? nextTags : current,
    );
  }, [initialData]);

  useEffect(() => {
    const next = getTrainingSnapshot(initialData, personalData.deporte);
    const previous = trainingBaselineRef.current;
    trainingBaselineRef.current = next;
    setTrainingData((current) => mergeUntouched(current, previous, next));
  }, [initialData, personalData.deporte]);

  const tabs = useMemo(() => {
    if (formMode === "personal") {
      return [{ id: "personal", label: "Datos Personales", icon: User }];
    }

    if (formMode === "exercises") {
      return [{ id: "entrenamiento", label: "Ejercicios", icon: Activity }];
    }

    return [
      { id: "personal", label: "Datos Personales", icon: User },
      { id: "entrenamiento", label: "Ejercicios", icon: Activity },
    ];
  }, [formMode]);

  useEffect(() => {
    const fallbackTab = tabs[0]?.id || "personal";
    const requestedTab = initialTab || fallbackTab;
    const nextTab = tabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : fallbackTab;
    setActiveTab(nextTab);
  }, [initialTab, tabs]);

  const trainingFieldsBySport = {
    Natación: [
      {
        id: "velocidad50m",
        title: "Velocidad 50m",
        subtitle: "Sprint de velocidad",
        unit: "Seg",
      },
      {
        id: "sprint25m",
        title: "Sprint 25m",
        subtitle: "Velocidad explosiva",
        unit: "Seg",
      },
      {
        id: "resistencia12min",
        title: "Resistencia 12min",
        subtitle: "Test de Cooper acuático",
        unit: "Metros",
      },
      {
        id: "seriesLargas",
        title: "Series largas",
        subtitle: "Resistencia en series",
        unit: "Min",
      },
      {
        id: "salidaViraje",
        title: "Salida/Viraje",
        subtitle: "Técnica de salida y viraje",
        unit: "Metros",
      },
      {
        id: "ralliesBrazadas",
        title: "Rallies de brazadas",
        subtitle: "Consistencia en brazadas",
        unit: "Min",
      },
    ],
    Ciclismo: [
      {
        id: "velocidadMaxima",
        title: "Velocidad Máxima",
        subtitle: "Velocidad pico alcanzada",
        unit: "km/h",
      },
      {
        id: "potenciaPromedio",
        title: "Potencia Promedio",
        subtitle: "Potencia media en entrenamiento",
        unit: "W",
      },
      {
        id: "distanciaRecorrida",
        title: "Distancia Recorrida",
        subtitle: "Distancia total del entrenamiento",
        unit: "km",
      },
      {
        id: "tiempoTotal",
        title: "Tiempo Total",
        subtitle: "Duración del entrenamiento",
        unit: "Min",
      },
      {
        id: "cadenciaPromedio",
        title: "Cadencia Promedio",
        subtitle: "RPM promedio",
        unit: "RPM",
      },
      {
        id: "elevacionGanada",
        title: "Elevación Ganada",
        subtitle: "Desnivel acumulado",
        unit: "m",
      },
    ],
    Fútbol: [
      {
        id: "velocidadSprint",
        title: "Velocidad Sprint",
        subtitle: "Velocidad máxima en sprint",
        unit: "km/h",
      },
      {
        id: "distanciaRecorrida",
        title: "Distancia Recorrida",
        subtitle: "Distancia total en partido",
        unit: "km",
      },
      {
        id: "sprintsRealizados",
        title: "Sprints Realizados",
        subtitle: "Número de sprints",
        unit: "unidades",
      },
      {
        id: "tiempoActivo",
        title: "Tiempo Activo",
        subtitle: "Tiempo en movimiento",
        unit: "Min",
      },
      {
        id: "aceleraciones",
        title: "Aceleraciones",
        subtitle: "Número de aceleraciones",
        unit: "unidades",
      },
      {
        id: "pasesCompletados",
        title: "Pases Completados",
        subtitle: "Precisión de pases",
        unit: "%",
      },
    ],
    Atletismo: [
      {
        id: "tiempo100m",
        title: "100m",
        subtitle: "Tiempo en 100 metros",
        unit: "Seg",
      },
      {
        id: "tiempo400m",
        title: "400m",
        subtitle: "Tiempo en 400 metros",
        unit: "Seg",
      },
      {
        id: "tiempo1500m",
        title: "1500m",
        subtitle: "Tiempo en 1500 metros",
        unit: "Min",
      },
      {
        id: "saltoLongitud",
        title: "Salto de Longitud",
        subtitle: "Distancia alcanzada",
        unit: "m",
      },
      {
        id: "lanzamientoPeso",
        title: "Lanzamiento de Peso",
        subtitle: "Distancia alcanzada",
        unit: "m",
      },
      {
        id: "resistenciaVO2",
        title: "VO2 Máximo",
        subtitle: "Consumo máximo de oxígeno",
        unit: "ml/kg/min",
      },
    ],
    Baloncesto: [
      {
        id: "tirosLibres",
        title: "Tiros Libres",
        subtitle: "Porcentaje de acierto",
        unit: "%",
      },
      {
        id: "tiros3Puntos",
        title: "Tiros de 3 Puntos",
        subtitle: "Porcentaje de acierto",
        unit: "%",
      },
      {
        id: "rebotes",
        title: "Rebotes",
        subtitle: "Rebotes capturados",
        unit: "unidades",
      },
      {
        id: "asistencias",
        title: "Asistencias",
        subtitle: "Número de asistencias",
        unit: "unidades",
      },
      {
        id: "robos",
        title: "Robos",
        subtitle: "Robos de balón",
        unit: "unidades",
      },
      {
        id: "tiempoJugado",
        title: "Tiempo Jugado",
        subtitle: "Minutos en cancha",
        unit: "Min",
      },
    ],
    Tenis: [
      {
        id: "velocidadServicio",
        title: "Velocidad de Servicio",
        subtitle: "Velocidad máxima del servicio",
        unit: "km/h",
      },
      {
        id: "aces",
        title: "Aces",
        subtitle: "Número de aces",
        unit: "unidades",
      },
      {
        id: "winners",
        title: "Winners",
        subtitle: "Golpes ganadores",
        unit: "unidades",
      },
      {
        id: "erroresNoForzados",
        title: "Errores No Forzados",
        subtitle: "Errores cometidos",
        unit: "unidades",
      },
      {
        id: "puntosGanados",
        title: "Puntos Ganados",
        subtitle: "Porcentaje de puntos ganados",
        unit: "%",
      },
      {
        id: "tiempoPartido",
        title: "Tiempo de Partido",
        subtitle: "Duración del partido",
        unit: "Min",
      },
    ],
    Voleibol: [
      {
        id: "ataquesExitosos",
        title: "Ataques Exitosos",
        subtitle: "Porcentaje de ataques exitosos",
        unit: "%",
      },
      {
        id: "bloqueos",
        title: "Bloqueos",
        subtitle: "Número de bloqueos",
        unit: "unidades",
      },
      {
        id: "saques",
        title: "Saques",
        subtitle: "Porcentaje de saques exitosos",
        unit: "%",
      },
      {
        id: "defensas",
        title: "Defensas",
        subtitle: "Número de defensas exitosas",
        unit: "unidades",
      },
      {
        id: "alturaSalto",
        title: "Altura de Salto",
        subtitle: "Altura máxima alcanzada",
        unit: "cm",
      },
      {
        id: "setsGanados",
        title: "Sets Ganados",
        subtitle: "Número de sets ganados",
        unit: "unidades",
      },
    ],
    Rugby: [
      {
        id: "placajes",
        title: "Placajes",
        subtitle: "Número de placajes exitosos",
        unit: "unidades",
      },
      {
        id: "metrosAvanzados",
        title: "Metros Avanzados",
        subtitle: "Distancia con balón",
        unit: "m",
      },
      {
        id: "pases",
        title: "Pases",
        subtitle: "Número de pases completados",
        unit: "unidades",
      },
      {
        id: "conversiones",
        title: "Conversiones",
        subtitle: "Porcentaje de conversiones",
        unit: "%",
      },
      {
        id: "lineouts",
        title: "Lineouts",
        subtitle: "Lineouts ganados",
        unit: "unidades",
      },
      {
        id: "tiempoJugado",
        title: "Tiempo Jugado",
        subtitle: "Minutos en campo",
        unit: "Min",
      },
    ],
    Boxeo: [
      {
        id: "golpesConectados",
        title: "Golpes Conectados",
        subtitle: "Número de golpes efectivos",
        unit: "unidades",
      },
      {
        id: "precisionGolpes",
        title: "Precisión de Golpes",
        subtitle: "Porcentaje de precisión",
        unit: "%",
      },
      {
        id: "roundsGanados",
        title: "Rounds Ganados",
        subtitle: "Número de rounds ganados",
        unit: "unidades",
      },
      {
        id: "defensasExitosas",
        title: "Defensas Exitosas",
        subtitle: "Número de defensas",
        unit: "unidades",
      },
      {
        id: "potenciaPromedio",
        title: "Potencia Promedio",
        subtitle: "Potencia media de golpes",
        unit: "kg",
      },
      {
        id: "tiempoEntrenamiento",
        title: "Tiempo de Entrenamiento",
        subtitle: "Duración del entrenamiento",
        unit: "Min",
      },
    ],
    Triatlón: [
      {
        id: "tiempoNatacion",
        title: "Tiempo Natación",
        subtitle: "Tiempo en segmento de natación",
        unit: "Min",
      },
      {
        id: "tiempoCiclismo",
        title: "Tiempo Ciclismo",
        subtitle: "Tiempo en segmento de ciclismo",
        unit: "Min",
      },
      {
        id: "tiempoCarrera",
        title: "Tiempo Carrera",
        subtitle: "Tiempo en segmento de carrera",
        unit: "Min",
      },
      {
        id: "tiempoTransicion",
        title: "Tiempo Transición",
        subtitle: "Tiempo total en transiciones",
        unit: "Seg",
      },
      {
        id: "velocidadPromedio",
        title: "Velocidad Promedio",
        subtitle: "Velocidad media total",
        unit: "km/h",
      },
      {
        id: "tiempoTotal",
        title: "Tiempo Total",
        subtitle: "Tiempo total de competencia",
        unit: "Min",
      },
    ],
    Crossfit: [
      {
        id: "rmSentadilla",
        title: "RM Sentadilla",
        subtitle: "Repetición máxima en sentadilla",
        unit: "kg",
      },
      {
        id: "rmPressBanca",
        title: "RM Press Banca",
        subtitle: "Repetición máxima en press banca",
        unit: "kg",
      },
      {
        id: "rmPesoMuerto",
        title: "RM Peso Muerto",
        subtitle: "Repetición máxima en peso muerto",
        unit: "kg",
      },
      {
        id: "tiempoFran",
        title: "Tiempo Fran",
        subtitle: "Tiempo en WOD Fran",
        unit: "Min",
      },
      {
        id: "tiempoMurph",
        title: "Tiempo Murph",
        subtitle: "Tiempo en WOD Murph",
        unit: "Min",
      },
      {
        id: "caloriasQuemadas",
        title: "Calorías Quemadas",
        subtitle: "Calorías totales quemadas",
        unit: "kcal",
      },
    ],
    Otro: [
      {
        id: "metrica1",
        title: "Métrica 1",
        subtitle: "Indicador personalizado 1",
        unit: "unidades",
      },
      {
        id: "metrica2",
        title: "Métrica 2",
        subtitle: "Indicador personalizado 2",
        unit: "unidades",
      },
      {
        id: "metrica3",
        title: "Métrica 3",
        subtitle: "Indicador personalizado 3",
        unit: "unidades",
      },
      {
        id: "metrica4",
        title: "Métrica 4",
        subtitle: "Indicador personalizado 4",
        unit: "unidades",
      },
      {
        id: "metrica5",
        title: "Métrica 5",
        subtitle: "Indicador personalizado 5",
        unit: "unidades",
      },
      {
        id: "metrica6",
        title: "Métrica 6",
        subtitle: "Indicador personalizado 6",
        unit: "unidades",
      },
    ],
  };

  const trainingSource =
    Object.keys(trainingFieldsFromDb).length > 0
      ? trainingFieldsFromDb
      : trainingFieldsBySport;

  const activeTrainingFieldTemplates = (() => {
    const sport = personalData.deporte;
    return trainingSource[sport] || trainingSource["Otro"] || [];
  })();

  const trainingFields = useMemo(
    () =>
      activeTrainingFieldTemplates.map((field) => ({
        ...field,
        required: false,
        value: trainingData[field.id] || "",
      })),
    [activeTrainingFieldTemplates, trainingData],
  );

  const hasTrainingChanges = useMemo(() => {
    if (!personalData.deporte) return false;

    const baseline = getTrainingSnapshot(initialData, personalData.deporte);

    return activeTrainingFieldTemplates.some(
      (field) =>
        normalizeValue(trainingData?.[field.id]) !==
        normalizeValue(baseline?.[field.id]),
    );
  }, [
    activeTrainingFieldTemplates,
    initialData,
    personalData.deporte,
    trainingData,
  ]);

  const countryKey = normalizeText(personalData.nacionalidad);
  const cityOptions =
    CITY_OPTIONS_BY_COUNTRY[countryKey] || CITY_OPTIONS_BY_COUNTRY.colombia;
  const filteredCityOptions = cityOptions
    .filter((city) =>
      normalizeText(city).includes(normalizeText(personalData.ciudad)),
    )
    .slice(0, 12);

  const handlePersonalInputChange = (field, value) => {
    const updated = {
      ...personalData,
      [field]: value,
    };

    if (field === "nacionalidad") {
      const nextCountryKey = normalizeText(value);
      const nextCityOptions =
        CITY_OPTIONS_BY_COUNTRY[nextCountryKey] || CITY_OPTIONS_BY_COUNTRY.colombia;

      if (
        updated.ciudad &&
        !nextCityOptions.some(
          (city) => normalizeText(city) === normalizeText(updated.ciudad),
        )
      ) {
        updated.ciudad = "";
      }
    }

    if (field === "peso" || field === "altura") {
      const peso =
        field === "peso" ? parseFloat(value) : parseFloat(updated.peso);
      const altura =
        field === "altura" ? parseFloat(value) : parseFloat(updated.altura);
      updated.imc = calculateBmi(peso, altura);
    }

    setPersonalData(updated);
  };

  const handleTrainingInputChange = (field, value) => {
    setTrainingData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSaveMessage("");
  };

  const handleReset = () => {
    setSaveMessage("");
    if (activeTab === "personal") {
      setPersonalData(buildFormData(initialData));
    } else if (activeTab === "entrenamiento") {
      setTrainingData(getTrainingSnapshot(initialData, personalData.deporte));
      setPersonalData(buildFormData(initialData));
    }
  };

  const handlePersonalSave = async () => {
    if (!athleteId) {
      setSaveMessage("No se encontró el perfil del atleta.");
      return;
    }

    const missingPersonalFields = [...REQUIRED_PERSONAL_FIELDS].filter(
      (field) => normalizeValue(personalData?.[field]) === "",
    );

    if (missingPersonalFields.length > 0) {
      setShowPersonalRequiredErrors(true);
      setSaveMessage("Completa los campos obligatorios antes de guardar.");
      return;
    }

    if (guardianNameError || guardianEmailError) {
      setShowPersonalRequiredErrors(true);
      setSaveMessage("Completa los campos obligatorios antes de guardar.");
      return;
    }

    setIsSaving(true);
    setSaveMessage("");
    try {
      const existingAttrs = parseAthleteAttributes(initialData.attributes);
      const calculatedBmi = calculateBmi(personalData.peso, personalData.altura);

      // Estructura de posición (Scout QA #24). Sincroniza
      // main_position/position_code con la posición elegida — antes editar el
      // perfil dejaba main_position con el valor viejo y el matching seguía
      // puntuando por una posición que el atleta ya no juega.
      const positionFields = buildPositionFields(
        positionCodeFromText(personalData.posicion),
      );
      const footballAttrs = {
        main_position: personalData.posicion || existingAttrs.main_position,
        position_code: positionFields.position_code,
        position_group: positionFields.position_group,
        tags,
      };

      const updatedAttrs = {
        ...existingAttrs,
        height: personalData.altura
          ? Number(personalData.altura)
          : existingAttrs.height,
        weight: personalData.peso
          ? Number(personalData.peso)
          : existingAttrs.weight,
        age: personalData.edad ? Number(personalData.edad) : existingAttrs.age,
        imc: calculatedBmi || personalData.imc || existingAttrs.imc,
        birth_date: personalData.fechaNacimiento || existingAttrs.birth_date,
        gender: personalData.genero || existingAttrs.gender,
        experience: personalData.experiencia || existingAttrs.experience,
        country: personalData.nacionalidad || existingAttrs.country,
        city: personalData.ciudad || existingAttrs.city,
        phone: personalData.telefono || existingAttrs.phone,
        sport: personalData.deporte || existingAttrs.sport,
        // El PATCH de atletas no acepta email como columna: se persiste dentro
        // de attributes (buildFormData ya lo lee desde ahí como fallback).
        email: personalData.email || existingAttrs.email,
        ...footballAttrs,
      };
      await doUpdateAthlete({
        id: athleteId,
        full_name: personalData.nombreCompleto || undefined,
        sport: personalData.deporte || undefined,
        position: personalData.posicion || undefined,
        category: personalData.nivel || undefined,
        // Solo menores: a un adulto no se le muestran estos campos, y reenviar
        // un valor viejo mal formado haría fallar su guardado.
        ...(isMinorProfile
          ? {
              guardian_name: normalizeValue(personalData.guardianName),
              guardian_email: guardianEmailValue,
            }
          : {}),
        attributes: updatedAttrs,
      });
      setSaveMessage("¡Datos guardados exitosamente!");
      onSaved?.();
    } catch (err) {
      setSaveMessage(err?.message || "Error al guardar. Intenta de nuevo.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(""), 4000);
    }
  };

  const handleTrainingSave = async () => {
    if (!athleteId) {
      setSaveMessage("No se encontró el perfil del atleta.");
      return;
    }

    if (!personalData.deporte) {
      setSaveMessage("Selecciona un deporte para guardar ejercicios.");
      return;
    }

    setIsSaving(true);
    setSaveMessage("");

    try {
      const nowIso = new Date().toISOString();
      const today = localDateKey();

      // Last-known training values (each section's newest row), used as the
      // base for field history. Only the training section is sent back, so
      // today's meals and check-in are never touched.
      const latestTrackingRow = getLatestTrackingRecord(initialData);

      const currentMetrics =
        latestTrackingRow?.metrics &&
        typeof latestTrackingRow.metrics === "object"
          ? latestTrackingRow.metrics
          : {};

      const currentTraining =
        currentMetrics.training && typeof currentMetrics.training === "object"
          ? currentMetrics.training
          : {};

      const currentBySport =
        currentTraining.bySport && typeof currentTraining.bySport === "object"
          ? currentTraining.bySport
          : {};

      const currentSportData =
        currentBySport[personalData.deporte] &&
        typeof currentBySport[personalData.deporte] === "object"
          ? currentBySport[personalData.deporte]
          : {};

      const currentFields =
        currentSportData.fields && typeof currentSportData.fields === "object"
          ? currentSportData.fields
          : {};

      const updatedFields = { ...currentFields };

      activeTrainingFieldTemplates.forEach((field) => {
        const nextValue = parseTrainingValue(
          trainingData?.[field.id],
          field.inputType,
        );

        const previousField =
          currentFields[field.id] && typeof currentFields[field.id] === "object"
            ? currentFields[field.id]
            : {};

        const previousHistory = Array.isArray(previousField.history)
          ? previousField.history
          : [];

        const previousValue = previousField?.latest?.value;
        const hasChanged =
          normalizeValue(previousValue) !== normalizeValue(nextValue);

        updatedFields[field.id] = {
          ...previousField,
          title: field.title,
          subtitle: field.subtitle || "",
          unit: field.unit || "",
          inputType: field.inputType || "text",
          latest: {
            value: nextValue,
            recordedAt: nowIso,
          },
          history: hasChanged
            ? [...previousHistory, { value: nextValue, recordedAt: nowIso }]
            : previousHistory,
        };
      });

      const updatedTraining = {
        ...currentTraining,
        lastSport: personalData.deporte,
        lastUpdatedAt: nowIso,
        bySport: {
          ...currentBySport,
          [personalData.deporte]: {
            ...currentSportData,
            updatedAt: nowIso,
            fields: updatedFields,
          },
        },
      };

      await doPatchTracking({
        date: today,
        metrics: { training: updatedTraining },
      });

      onSaved?.();

      setTrainingData(
        getTrainingSnapshotFromMetrics(
          { training: updatedTraining },
          personalData.deporte,
        ),
      );
      setSaveMessage("¡Ejercicios guardados exitosamente!");
    } catch (error) {
      setSaveMessage(error?.message || "Error al guardar ejercicios.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(""), 4000);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Tabs Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors duration-200 border-b-2 sm:px-4 sm:text-base ${
                  isActive
                    ? "border-athletain-blue text-athletain-blue bg-gray-50"
                    : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                }`}
              >
                <Icon
                  className={`w-5 h-5 ${isActive ? "text-athletain-blue" : "text-gray-500"}`}
                />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        {activeTab === "personal" && (
          <div>
            {/* Personal Data Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Nombre Completo */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Nombre Completo
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">
                    Nombre y apellidos del deportista
                  </p>
                </div>
                <input
                  type="text"
                  value={personalData.nombreCompleto}
                  onChange={(e) =>
                    handlePersonalInputChange("nombreCompleto", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("nombreCompleto"),
                    "w-full",
                  )}
                  placeholder="Ej: Juan Pérez"
                />
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("nombreCompleto"),
                )}
              </div>

              {/* Fecha de Nacimiento */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Fecha de Nacimiento
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">Fecha de nacimiento</p>
                </div>
                <input
                  type="date"
                  value={personalData.fechaNacimiento}
                  onChange={(e) =>
                    handlePersonalInputChange("fechaNacimiento", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("fechaNacimiento"),
                    "block w-full min-w-0 appearance-none",
                  )}
                />
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("fechaNacimiento"),
                )}
              </div>

              {/* Edad */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Edad
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">Edad actual</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={personalData.edad}
                    onChange={(e) =>
                      handlePersonalInputChange("edad", e.target.value)
                    }
                    className={inputClassName(
                      showPersonalRequiredErrors &&
                        isPersonalFieldMissing("edad"),
                      "flex-1",
                    )}
                    placeholder=""
                    min="1"
                    max="120"
                  />
                  <span className="text-sm text-gray-600 font-medium">
                    años
                  </span>
                </div>
                {requiredHint(
                  showPersonalRequiredErrors && isPersonalFieldMissing("edad"),
                )}
              </div>

              {/* Género */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Género
                  </h3>
                  <p className="text-sm text-gray-600">Género del deportista</p>
                </div>
                <select
                  value={personalData.genero}
                  onChange={(e) =>
                    handlePersonalInputChange("genero", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-athletain-blue focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="">Selecciona</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                  <option value="otro">Otro</option>
                  <option value="prefiero-no-decir">Prefiero no decir</option>
                </select>
              </div>

              {/* Peso */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Peso
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">Peso corporal actual</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={personalData.peso}
                    onChange={(e) =>
                      handlePersonalInputChange("peso", e.target.value)
                    }
                    className={inputClassName(
                      showPersonalRequiredErrors &&
                        isPersonalFieldMissing("peso"),
                      "flex-1",
                    )}
                    placeholder=""
                    min="1"
                    step="0.1"
                  />
                  <span className="text-sm text-gray-600 font-medium">kg</span>
                </div>
                {requiredHint(
                  showPersonalRequiredErrors && isPersonalFieldMissing("peso"),
                )}
              </div>

              {/* Altura */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Altura
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">
                    Estatura del deportista
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={personalData.altura}
                    onChange={(e) =>
                      handlePersonalInputChange("altura", e.target.value)
                    }
                    className={inputClassName(
                      showPersonalRequiredErrors &&
                        isPersonalFieldMissing("altura"),
                      "flex-1",
                    )}
                    placeholder=""
                    min="1"
                    step="0.1"
                  />
                  <span className="text-sm text-gray-600 font-medium">cm</span>
                </div>
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("altura"),
                )}
              </div>

              {/* IMC (Calculado automáticamente) */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Índice de Masa Corporal
                  </h3>
                  <p className="text-sm text-gray-600">
                    Calculado automáticamente
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={personalData.imc || ""}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    placeholder="Se calcula automáticamente"
                  />
                  <span className="text-sm text-gray-600 font-medium">
                    kg/m²
                  </span>
                </div>
              </div>

              {/* Deporte */}
              {/* Posición */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Posición
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">
                    Posición o especialidad
                  </p>
                </div>
                {/* Select cerrado sobre la taxonomía de fútbol, para que la
                    posición mapee siempre a un code. */}
                <select
                  value={personalData.posicion}
                  onChange={(e) =>
                    handlePersonalInputChange("posicion", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("posicion"),
                    "w-full bg-white",
                  )}
                >
                  <option value="">Selecciona una posición</option>
                  {FOOTBALL_POSITIONS.map((pos) => (
                    <option key={pos.code} value={pos.label}>
                      {pos.label}
                    </option>
                  ))}
                </select>

                <div className="mt-4 border-t border-gray-100 pt-4">
                  <TagSelector value={tags} onChange={setTags} />
                </div>
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("posicion"),
                )}
              </div>

              {/* Nivel */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Nivel
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">Nivel de competencia</p>
                </div>
                <select
                  value={personalData.nivel}
                  onChange={(e) =>
                    handlePersonalInputChange("nivel", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("nivel"),
                    "w-full bg-white",
                  )}
                >
                  <option value="">Selecciona</option>
                  <option value="Principiante">Principiante</option>
                  <option value="Intermedio">Intermedio</option>
                  <option value="Avanzado">Avanzado</option>
                  <option value="Profesional">Profesional</option>
                  <option value="Élite">Élite</option>
                </select>
                {requiredHint(
                  showPersonalRequiredErrors && isPersonalFieldMissing("nivel"),
                )}
              </div>

              {/* Años de Experiencia */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Años de Experiencia
                  </h3>
                  <p className="text-sm text-gray-600">
                    Tiempo practicando el deporte
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={personalData.experiencia}
                    onChange={(e) =>
                      handlePersonalInputChange("experiencia", e.target.value)
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    placeholder=""
                    min="0"
                  />
                  <span className="text-sm text-gray-600 font-medium">
                    años
                  </span>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Email
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">
                    Correo electrónico de contacto
                  </p>
                </div>
                <input
                  type="email"
                  value={personalData.email}
                  onChange={(e) =>
                    handlePersonalInputChange("email", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("email"),
                    "w-full",
                  )}
                  placeholder="ejemplo@email.com"
                />
                {requiredHint(
                  showPersonalRequiredErrors && isPersonalFieldMissing("email"),
                )}
              </div>

              {/* Teléfono */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Teléfono
                  </h3>
                  <p className="text-sm text-gray-600">Número de contacto</p>
                </div>
                <input
                  type="tel"
                  value={personalData.telefono}
                  onChange={(e) =>
                    handlePersonalInputChange("telefono", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-athletain-blue focus:border-transparent text-gray-900"
                  placeholder="+57 300 123 4567"
                />
              </div>

              {/* Nacionalidad */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Nacionalidad
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">País de origen</p>
                </div>
                <input
                  type="text"
                  value={personalData.nacionalidad}
                  onChange={(e) =>
                    handlePersonalInputChange("nacionalidad", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("nacionalidad"),
                    "w-full",
                  )}
                  placeholder="Ej: Colombia"
                />
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("nacionalidad"),
                )}
              </div>

              {/* Ciudad */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Ciudad
                    <RequiredMark />
                  </h3>
                  <p className="text-sm text-gray-600">Ciudad de residencia</p>
                </div>
                <input
                  type="text"
                  list="athlete-city-options"
                  value={personalData.ciudad}
                  onChange={(e) =>
                    handlePersonalInputChange("ciudad", e.target.value)
                  }
                  className={inputClassName(
                    showPersonalRequiredErrors &&
                      isPersonalFieldMissing("ciudad"),
                    "w-full",
                  )}
                  placeholder="Ej: Bogotá"
                />
                <datalist id="athlete-city-options">
                  {filteredCityOptions.map((city) => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
                {requiredHint(
                  showPersonalRequiredErrors &&
                    isPersonalFieldMissing("ciudad"),
                )}
              </div>
            </div>

            {/* Guardian section — visible when athlete is a minor */}
            {(Number(personalData.edad) > 0 && Number(personalData.edad) < 18) && (
              <div className="mt-6">
                <h2 className="text-lg font-bold text-gray-900">Tutor Legal</h2>
                <p className="mt-1 mb-4 text-sm text-gray-600">
                  Como eres menor de edad, cuando un scout quiera contactarte le
                  enviaremos la solicitud a este correo. Solo podrás hablar con el
                  scout si tu tutor la aprueba.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900">Nombre del tutor<RequiredMark /></h3>
                      <p className="text-sm text-gray-600">Nombre completo del tutor legal</p>
                    </div>
                    <input
                      type="text"
                      value={personalData.guardianName || ""}
                      onChange={(e) => handlePersonalInputChange("guardianName", e.target.value)}
                      className={inputClassName(
                        showPersonalRequiredErrors && Boolean(guardianNameError),
                        "w-full",
                      )}
                      placeholder="Nombre del tutor"
                    />
                    {showPersonalRequiredErrors && guardianNameError && (
                      <p className="mt-2 text-xs font-medium text-red-600">{guardianNameError}</p>
                    )}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-gray-900">Email del tutor<RequiredMark /></h3>
                      <p className="text-sm text-gray-600">Correo del tutor legal</p>
                    </div>
                    <input
                      type="email"
                      value={personalData.guardianEmail || ""}
                      onChange={(e) => handlePersonalInputChange("guardianEmail", e.target.value)}
                      className={inputClassName(
                        showPersonalRequiredErrors && Boolean(guardianEmailError),
                        "w-full",
                      )}
                      placeholder="correo@ejemplo.com"
                    />
                    {showPersonalRequiredErrors && guardianEmailError && (
                      <p className="mt-2 text-xs font-medium text-red-600">{guardianEmailError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
              {saveMessage && (
                <p
                  className={`text-sm text-center font-medium ${
                    saveMessage.includes("Error") ||
                    saveMessage.includes("No se") ||
                    saveMessage.includes("Completa")
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {saveMessage}
                </p>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                >
                  ANTERIOR
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full sm:w-auto px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium"
                  >
                    RESETEAR
                  </button>
                  {hasPersonalChanges && (
                    <button
                      type="button"
                      onClick={handlePersonalSave}
                      disabled={isSaving}
                      className="w-full sm:w-auto px-8 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors duration-200 font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          Guardando
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </>
                      ) : (
                        "GUARDAR DATOS"
                      )}
                    </button>
                  )}
                  {tabs.some((tab) => tab.id === "entrenamiento") && (
                    <button
                      type="button"
                      onClick={() => setActiveTab("entrenamiento")}
                      className="w-full sm:w-auto px-6 py-3 bg-athletain-blue text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium flex items-center justify-center gap-2"
                    >
                      SIGUIENTE
                      <Grid className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "entrenamiento" && (
          <div>
            {!personalData.deporte ? (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center shadow-sm">
                <p className="text-gray-600 mb-4">
                  Completa el deporte en Mis Datos para ver los indicadores de
                  ejercicios específicos.
                </p>
              </div>
            ) : (
              <>
                {/* Deporte seleccionado info */}
                <div className="mb-4 p-4 bg-gray-100 border border-gray-200 rounded-xl">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">
                      Deporte seleccionado:
                    </span>{" "}
                    <span className="text-athletain-blue font-medium">
                      {personalData.deporte}
                    </span>
                  </p>
                </div>

                {/* Training Cards Grid */}
                <div className="mb-5 border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800">
                  El registro de ejercicios es diario: el dia que no se
                  registre no se cuenta para el progreso.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {trainingFields.map((field) => (
                    <div
                      key={field.id}
                      className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm"
                    >
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-gray-900">
                          {field.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {field.subtitle}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type={
                            field.inputType === "number" ? "number" : "text"
                          }
                          value={field.value}
                          onChange={(e) =>
                            handleTrainingInputChange(field.id, e.target.value)
                          }
                          className={inputClassName(false, "flex-1")}
                          placeholder=""
                        />
                        <span className="text-sm text-gray-600 font-medium">
                          {field.unit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
              {saveMessage && (
                <p
                  className={`text-sm text-center font-medium ${
                    saveMessage.includes("Error") ||
                    saveMessage.includes("No se") ||
                    saveMessage.includes("Completa") ||
                    saveMessage.includes("Selecciona")
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {saveMessage}
                </p>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                >
                  RESETEAR
                </button>
                {hasTrainingChanges && (
                  <button
                    type="button"
                    onClick={handleTrainingSave}
                    disabled={isSaving}
                    className="w-full sm:w-auto px-8 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors duration-200 font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        Guardando
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </>
                    ) : (
                      "GUARDAR EJERCICIOS"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AthleteForm;
